import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import PublicNavigation from '../../components/ui/PublicNavigation';
import UnifiedFooter from '../../components/ui/UnifiedFooter';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ContactSection from '../shipping-information/components/ContactSection';

const MaritimeTransportBlogArticle = () => {
  const [copiedLink, setCopiedLink] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(window.location?.href);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const tableOfContents = [
  { id: 'pourquoi', title: 'Pourquoi choisir le transport maritime vers Abidjan ?' },
  { id: 'comment', title: 'Comment ça fonctionne avec ADT Express ?' },
  { id: 'delais', title: 'Quels sont les délais de livraison ?' },
  { id: 'prix', title: 'Combien coûte un envoi vers Abidjan ?' },
  { id: 'diaspora', title: 'La diaspora ivoirienne nous fait confiance' }];


  const relatedArticles = [
  {
    title: "Les 10 choses à savoir avant d\'envoyer un colis en Côte d\'Ivoire",
    category: "Guide Pratique",
    date: "5 Février 2026",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f2a6f50a-1772217450034.png",
    excerpt: "Découvrez les points essentiels à connaître pour un envoi réussi."
  },
  {
    title: "Barrique noire vs barrique bleue : laquelle choisir ?",
    category: "Guide Pratique",
    date: "28 Janvier 2026",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1284b4e14-1770849477282.png",
    excerpt: "Comparatif détaillé pour vous aider à choisir la barrique adaptée."
  },
  {
    title: "Témoignage : Comment j\'ai envoyé 3 barriques à ma famille",
    category: "Témoignages",
    date: "20 Janvier 2026",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b579e038-1780788246471.png",
    excerpt: "L'expérience d'Aminata, cliente fidèle d'ADT Express depuis 2 ans."
  }];


  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Transport maritime vers Abidjan : comment envoyer vos affaires depuis Paris ?",
    "description": "Découvrez comment envoyer vos affaires de Paris à Abidjan par transport maritime. Guide complet : barriques, conteneurs, délais, tarifs.",
    "image": "https://images.unsplash.com/photo-1431610792557-0e2deb7199dd",
    "author": {
      "@type": "Organization",
      "name": "ADT Express"
    },
    "publisher": {
      "@type": "Organization",
      "name": "ADT Express",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.adtexpress.fr/logo.png"
      }
    },
    "datePublished": "2026-02-11",
    "dateModified": "2026-02-11",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://www.adtexpress.fr/blog/transport-maritime-vers-abidjan"
    }
  };

  return (
    <>
      <Helmet>
        <title>Transport maritime vers Abidjan : comment envoyer vos affaires depuis Paris ? | Blog ADT Express</title>
        <meta name="description" content="Découvrez comment envoyer vos affaires de Paris à Abidjan par transport maritime. Guide complet : barriques, conteneurs, délais, tarifs, dédouanement. ADT Express, partenaire de la diaspora ivoirienne." />
        <meta name="keywords" content="transport maritime Abidjan, envoyer affaires Paris Abidjan, conteneur Paris Abidjan, fret maritime Côte d'Ivoire, expédition bateau France Abidjan, diaspora ivoirienne, envoi colis maritime" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <div className="min-h-screen bg-white">
        <PublicNavigation />

        {/* Breadcrumb Trail */}
        <div className="bg-gray-50 border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="flex items-center gap-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-green-700">Accueil</Link>
              <Icon name="ChevronRight" className="w-4 h-4" />
              <Link to="/maritime-transport-blog-hub" className="hover:text-green-700">Blog</Link>
              <Icon name="ChevronRight" className="w-4 h-4" />
              <span className="text-gray-900 font-medium">Transport Maritime</span>
              <Icon name="ChevronRight" className="w-4 h-4" />
              <span className="text-gray-500 truncate">Transport maritime vers Abidjan</span>
            </nav>
          </div>
        </div>

        {/* Article Container - 70/30 Layout */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-[1fr_350px] gap-12">
            {/* Main Content - 70% */}
            <article className="max-w-none">
              {/* Article Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8">
                
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  Transport maritime vers Abidjan : comment envoyer vos affaires depuis Paris ?
                </h1>
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-6">
                  <span className="flex items-center gap-1">
                    <Icon name="Calendar" className="w-4 h-4" />
                    11 Février 2026
                  </span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Icon name="Clock" className="w-4 h-4" />
                    8 min de lecture
                  </span>
                  <span>•</span>
                  <span className="bg-green-100 text-green-700 px-3 py-1 rounded-md font-medium">
                    Guide Pratique
                  </span>
                </div>
              </motion.div>

              {/* Article Hero Image */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mb-12 rounded-xl overflow-hidden">
                
                <img
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_10956ea78-1780788246594.png"
                  alt="Conteneur maritime au port d'Anvers pour envoi vers Abidjan"
                  className="w-full h-auto" />
                
              </motion.div>

              {/* Article Content */}
              <div className="prose prose-lg max-w-none">
                {/* Section 1 */}
                <section id="pourquoi" className="mb-12 scroll-mt-24">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-start gap-4">
                    <div className="w-1 h-8 bg-green-700 rounded-full flex-shrink-0 mt-1" />
                    <span>Pourquoi choisir le transport maritime vers Abidjan ?</span>
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-6" style={{ fontSize: '16px', lineHeight: '1.7' }}>
                    Le transport maritime est le moyen le plus abordable pour envoyer des volumes importants. Contrairement au fret aérien, qui coûte très cher au kilo, le transport par conteneur vous permet d'envoyer des barriques entières d'affaires à un prix raisonnable.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-8" style={{ fontSize: '16px', lineHeight: '1.7' }}>
                    Chez ADT Express, nous organisons <strong>2 départs par mois</strong> depuis Paris vers Abidjan. Vos affaires voyagent dans un conteneur sécurisé, à l'abri des intempéries.
                  </p>

                  {/* Info Box */}
                  <div className="bg-green-50 border-l-4 border-green-700 p-6 rounded-r-lg mb-8">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-green-700 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="Lightbulb" className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2 text-lg">Le saviez-vous ?</h4>
                        <p className="text-gray-700" style={{ fontSize: '16px', lineHeight: '1.7' }}>
                          Avec 2 départs par mois, ADT Express assure une rotation régulière entre Paris et Abidjan. Cela signifie que vous n'avez jamais longtemps à attendre pour le prochain envoi !
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Section 2 */}
                <section id="comment" className="mb-12 scroll-mt-24">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-start gap-4">
                    <div className="w-1 h-8 bg-green-700 rounded-full flex-shrink-0 mt-1" />
                    <span>Comment ça fonctionne avec ADT Express ?</span>
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-6" style={{ fontSize: '16px', lineHeight: '1.7' }}>
                    Le processus est très simple. Vous commandez une barrique vide (noire ou bleue) auprès de notre équipe. Nous vous la livrons à Paris ou en banlieue. Vous la remplissez avec tout ce que vous souhaitez envoyer : vêtements, chaussures, électroménager, produits alimentaires non périssables, jouets, et bien plus encore.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-8" style={{ fontSize: '16px', lineHeight: '1.7' }}>
                    Une fois votre barrique prête, vous nous contactez pour organiser la collecte. Nous récupérons votre barrique et la plaçons dans notre conteneur maritime direction Abidjan.
                  </p>
                </section>

                {/* Section 3 */}
                <section id="delais" className="mb-12 scroll-mt-24">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-start gap-4">
                    <div className="w-1 h-8 bg-green-700 rounded-full flex-shrink-0 mt-1" />
                    <span>Quels sont les délais de livraison ?</span>
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-8" style={{ fontSize: '16px', lineHeight: '1.7' }}>
                    Le transport maritime entre la France et la Côte d'Ivoire prend généralement entre <strong>3 et 5 semaines</strong> selon les conditions de navigation. Avec 2 départs par mois, vous n'avez jamais longtemps à attendre pour le prochain envoi.
                  </p>
                </section>

                {/* Section 4 */}
                <section id="prix" className="mb-12 scroll-mt-24">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-start gap-4">
                    <div className="w-1 h-8 bg-green-700 rounded-full flex-shrink-0 mt-1" />
                    <span>Combien coûte un envoi vers Abidjan ?</span>
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-6" style={{ fontSize: '16px', lineHeight: '1.7' }}>
                    Nos tarifs commencent à partir de 165€ tout compris (barrique + transport) pour un envoi depuis Paris intra-muros. Consultez notre <Link to="/dedicated-pricing-page" className="text-green-700 hover:text-green-800 font-semibold underline">page tarifs</Link> pour voir le détail des prix.
                  </p>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start gap-3 text-gray-700" style={{ fontSize: '16px', lineHeight: '1.7' }}>
                      <Icon name="CheckCircle" className="w-5 h-5 text-green-700 flex-shrink-0 mt-1" />
                      <span><strong>Barrique noire</strong> : 25€ (Paris) ou 30€ (Banlieue) + 140€ de transport = Total à partir de 165€</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-700" style={{ fontSize: '16px', lineHeight: '1.7' }}>
                      <Icon name="CheckCircle" className="w-5 h-5 text-green-700 flex-shrink-0 mt-1" />
                      <span><strong>Barrique bleue</strong> : 40€ + 130€ de transport = Total à partir de 170€</span>
                    </li>
                  </ul>
                </section>

                {/* Section 5 */}
                <section id="diaspora" className="mb-12 scroll-mt-24">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-start gap-4">
                    <div className="w-1 h-8 bg-green-700 rounded-full flex-shrink-0 mt-1" />
                    <span>La diaspora ivoirienne nous fait confiance</span>
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-6" style={{ fontSize: '16px', lineHeight: '1.7' }}>
                    Chaque mois, des dizaines de familles de la diaspora ivoirienne en Île-de-France font appel à ADT Express pour envoyer des colis à leurs proches à Abidjan. Notre service est pensé pour être simple, fiable et accessible.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-8" style={{ fontSize: '16px', lineHeight: '1.7' }}>
                    Nous comprenons l'importance de maintenir le lien avec vos proches restés au pays. C'est pourquoi nous mettons tout en œuvre pour que vos envois arrivent en parfait état et dans les délais annoncés.
                  </p>
                </section>

                {/* CTA Section */}
                <div className="bg-green-700 rounded-xl p-8 text-center mb-12">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Prêt à envoyer vos affaires en Côte d'Ivoire ?
                  </h3>
                  <p className="text-white/90 mb-6" style={{ fontSize: '16px' }}>
                    Contactez ADT Express dès maintenant pour commander votre barrique et réserver votre place dans le prochain conteneur.
                  </p>
                  <Link to="/dedicated-pricing-page">
                    <Button className="bg-white text-green-700 hover:bg-gray-100 font-semibold px-8 py-3 mb-3">
                      Commander ma barrique
                    </Button>
                  </Link>
                  <p className="text-white/90 text-sm">Prochain départ : 25 février 2026</p>
                </div>
              </div>

              {/* Tags */}
              <div className="mt-12 pt-8 border-t">
                <p className="text-sm font-semibold text-gray-600 mb-3">Tags :</p>
                <div className="flex flex-wrap gap-2">
                  {['transport maritime', 'Abidjan', 'diaspora ivoirienne', 'guide pratique']?.map((tag, index) =>
                  <span
                    key={index}
                    className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm hover:bg-green-100 hover:text-green-700 cursor-pointer transition-colors">
                    
                      {tag}
                    </span>
                  )}
                </div>
              </div>

              {/* Social Share */}
              <div className="mt-8 pt-8 border-t">
                <p className="text-sm font-semibold text-gray-600 mb-4">Partager cet article :</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${window.location?.href}`, '_blank')}
                    className="w-10 h-10 bg-gray-200 hover:bg-blue-600 text-gray-600 hover:text-white rounded-full flex items-center justify-center transition-all"
                    aria-label="Partager sur Facebook">
                    
                    <Icon name="Facebook" className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => window.open(`https://twitter.com/intent/tweet?url=${window.location?.href}`, '_blank')}
                    className="w-10 h-10 bg-gray-200 hover:bg-sky-500 text-gray-600 hover:text-white rounded-full flex items-center justify-center transition-all"
                    aria-label="Partager sur Twitter">
                    
                    <Icon name="Twitter" className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => window.open(`https://wa.me/?text=${window.location?.href}`, '_blank')}
                    className="w-10 h-10 bg-gray-200 hover:bg-green-600 text-gray-600 hover:text-white rounded-full flex items-center justify-center transition-all"
                    aria-label="Partager sur WhatsApp">
                    
                    <Icon name="MessageCircle" className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleCopyLink}
                    className="w-10 h-10 bg-gray-200 hover:bg-gray-600 text-gray-600 hover:text-white rounded-full flex items-center justify-center transition-all relative"
                    aria-label="Copier le lien">
                    
                    <Icon name="Link" className="w-5 h-5" />
                    {copiedLink &&
                    <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-3 py-1 rounded whitespace-nowrap">
                        Lien copié !
                      </span>
                    }
                  </button>
                </div>
              </div>
            </article>

            {/* Sidebar - 30% */}
            <aside className="lg:sticky lg:top-24 h-fit space-y-6">
              {/* Table of Contents */}
              <div className="bg-gray-50 rounded-xl p-6 border">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Icon name="List" className="w-5 h-5 text-green-700" />
                  Sommaire
                </h3>
                <nav className="space-y-3">
                  {tableOfContents?.map((item, index) =>
                  <a
                    key={index}
                    href={`#${item?.id}`}
                    className="block text-sm text-gray-700 hover:text-green-700 hover:translate-x-1 transition-all">
                    
                      {item?.title}
                    </a>
                  )}
                </nav>
              </div>

              {/* Next Departure */}
              <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Icon name="Ship" className="w-5 h-5 text-green-700" />
                  Prochain départ
                </h3>
                <p className="text-2xl font-bold text-green-700 mb-4">25 février 2026</p>
                <Link to="/dedicated-pricing-page">
                  <Button className="w-full bg-green-700 hover:bg-green-800 text-white font-semibold">
                    Réserver
                  </Button>
                </Link>
              </div>

              {/* Recommended Articles */}
              <div className="bg-white rounded-xl p-6 border">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Icon name="BookOpen" className="w-5 h-5 text-green-700" />
                  Articles recommandés
                </h3>
                <div className="space-y-4">
                  {relatedArticles?.slice(0, 2)?.map((article, index) =>
                  <Link key={index} to="/maritime-transport-blog-article" className="block group">
                      <div className="flex gap-3">
                        <img
                        src={article?.image}
                        alt={article?.title}
                        className="w-20 h-20 object-cover rounded-lg flex-shrink-0" />
                      
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 group-hover:text-green-700 line-clamp-2 mb-1">
                            {article?.title}
                          </h4>
                          <p className="text-xs text-gray-500">{article?.date}</p>
                        </div>
                      </div>
                    </Link>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </div>

        {/* Related Articles Section */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Articles recommandés
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedArticles?.map((article, index) =>
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}>
                
                  <Link to="/maritime-transport-blog-article">
                    <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                      <div className="h-48 overflow-hidden">
                        <img
                        src={article?.image}
                        alt={article?.title}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-300" />
                      
                      </div>
                      <div className="p-6 flex flex-col flex-grow">
                        <span className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-md text-xs font-medium mb-3 w-fit">
                          {article?.category}
                        </span>
                        <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2">
                          {article?.title}
                        </h3>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2 flex-grow">
                          {article?.excerpt}
                        </p>
                        <div className="flex items-center gap-2 text-green-700 font-semibold text-sm">
                          <span>Lire l'article</span>
                          <Icon name="ArrowRight" className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              )}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <ContactSection />
        </div>
      </div>
      <UnifiedFooter />
    </>);







};

export default MaritimeTransportBlogArticle;